import React, { useEffect, useRef, useState } from 'react';
import { FiList, FiPlus, FiX } from 'react-icons/fi';
import InlineLoading from './InlineLoading';

const TONE_STYLES = {
  navbar: {
    activeTab: 'bg-gray-800/90 border-gray-800 text-white',
    activeMeta: 'text-gray-200 group-hover:text-white',
    activeDelete: 'text-gray-200 hover:text-white',
    activeNewButton: 'bg-gray-800/90 border-gray-800 text-white',
  },
  blue: {
    activeTab: 'bg-blue-600 border-blue-700 text-white',
    activeMeta: 'text-blue-100 group-hover:text-white',
    activeDelete: 'text-blue-100 hover:text-white',
    activeNewButton: 'bg-blue-600 border-blue-700 text-white',
  },
  neutral: {
    activeTab: 'bg-white border-blue-400 text-blue-700',
    activeMeta: 'text-gray-500 group-hover:text-gray-600',
    activeDelete: 'text-gray-400 hover:text-red-600',
    activeNewButton: 'bg-blue-500 border-blue-500 text-white',
  },
};

export const getDocumentTabToneStyles = (tone = 'neutral') =>
  TONE_STYLES[tone] || TONE_STYLES.neutral;

const idsMatch = (a, b) => {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return String(a) === String(b);
};

const DocumentTabs = ({
  tabs = [],
  activeId = null,
  activeNew = false,
  onSelect,
  onNew,
  onDelete,
  canDeleteTab = () => true,
  canCreate = true,
  canSelect = true,
  canDelete = true,
  loading = false,
  error = null,
  menuTabs = null,
  menuLoading = false,
  menuError = null,
  onListOpen,
  onMenuSelect,
  scopeReady = true,
  scopeLoadingLabel = 'Cargando pendientes',
  tone = 'neutral',
  stickyTopClassName = 'top-0',
  newTitle = 'Nuevo',
  newDisabled = false,
  newDisabledMessage = '',
  listTitle = 'Ver pendientes',
  menuTitle = 'Pendientes',
  emptyMessage = 'No hay pendientes.',
  getTabTitle = (item) => `#${item?.id}`,
  getTabMeta = () => '',
  getMenuTabTitle = null,
  getMenuTabMeta = null,
  toolbarActions = null,
}) => {
  const [showAll, setShowAll] = useState(false);
  const allMenuRef = useRef(null);
  const toneStyles = getDocumentTabToneStyles(tone);
  const menuItems = menuTabs || tabs;
  const resolveMenuTitle = getMenuTabTitle || getTabTitle;
  const resolveMenuMeta = getMenuTabMeta || getTabMeta;

  useEffect(() => {
    if (!showAll) return;
    const handleClick = (event) => {
      if (allMenuRef.current && !allMenuRef.current.contains(event.target)) {
        setShowAll(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showAll]);

  const handleSelect = (item) => {
    if (!canSelect) return;
    onSelect?.(item);
  };

  const handleNew = () => {
    if (!canCreate) return;
    onNew?.();
  };

  return (
    <div className={`py-2 sticky ${stickyTopClassName} z-[900] w-full bg-gray-100 border-b border-gray-200 text-xs sm:text-sm text-gray-800`}>
      <div className="flex items-start justify-between px-1.5 py-1">
        <div className="flex-1 min-w-0 overflow-x-auto">
          <div className="flex items-end gap-1 flex-nowrap w-max">
            {tabs.map((tab) => {
              const isActive = idsMatch(activeId, tab?.id);
              const meta = getTabMeta(tab);
              const tabCanDelete = canDelete && canDeleteTab(tab);
              return (
                <div
                  key={tab.id}
                  className={`group flex items-center max-w-xs border border-b-0 rounded-t-md whitespace-nowrap ${
                    isActive
                      ? toneStyles.activeTab
                      : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-white hover:text-gray-800'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleSelect(tab)}
                    className="px-3 py-1 text-left truncate"
                  >
                    <span className="font-medium">{getTabTitle(tab)}</span>
                    {meta && (
                      <span
                        className={`ml-1 ${
                          isActive
                            ? toneStyles.activeMeta
                            : 'text-gray-500 group-hover:text-gray-600'
                        }`}
                      >
                        · {meta}
                      </span>
                    )}
                  </button>
                  {tabCanDelete && (
                    <button
                      type="button"
                      onClick={() => onDelete?.(tab)}
                      className={`px-1.5 py-1 flex items-center ${
                        isActive
                          ? toneStyles.activeDelete
                          : 'text-gray-400 hover:text-red-600'
                      }`}
                    >
                      <FiX size={11} />
                    </button>
                  )}
                </div>
              );
            })}

            {loading && (
              <span className="text-gray-400 whitespace-nowrap px-2 py-1">
                <InlineLoading />
              </span>
            )}

            {!scopeReady && (
              <span className="text-gray-400 whitespace-nowrap px-2 py-1">
                <InlineLoading label={scopeLoadingLabel} />
              </span>
            )}

            {error && !loading && (
              <span className="text-red-500 whitespace-nowrap px-2 py-1">
                Error al cargar
              </span>
            )}
          </div>
        </div>

        <div className="relative flex items-start gap-1 flex-shrink-0 ml-2 self-start">
          {toolbarActions}

          {canCreate && (
            <button
              type="button"
              onClick={handleNew}
              title={newDisabled ? newDisabledMessage : newTitle}
              className={`inline-flex items-center justify-center w-7 h-7 border rounded-md ${
                activeNew
                  ? toneStyles.activeNewButton
                  : newDisabled
                    ? 'bg-gray-200 border-gray-300 text-gray-400 cursor-not-allowed'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-blue-50 hover:text-blue-600'
              }`}
              aria-disabled={newDisabled}
            >
              <FiPlus size={14} />
            </button>
          )}

          <button
            type="button"
            onClick={() => setShowAll((prev) => {
              const next = !prev;
              if (next) onListOpen?.();
              return next;
            })}
            title={listTitle}
            className="inline-flex items-center justify-center w-7 h-7 border border-gray-300 rounded-md bg-white text-gray-600 hover:bg-gray-100"
          >
            <FiList size={14} />
          </button>

          {showAll && (
            <div
              ref={allMenuRef}
              className="absolute right-0 top-full z-[1000] mt-1 w-72 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg"
            >
              <div className="px-3 py-2 border-b text-xs font-semibold text-gray-700">
                {menuTitle}
              </div>
              {menuLoading ? (
                <div className="px-3 py-3 text-xs text-gray-500">
                  <InlineLoading label="Cargando ordenes" />
                </div>
              ) : menuError ? (
                <div className="px-3 py-3 text-xs text-red-600">
                  {menuError}
                </div>
              ) : menuItems.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-500">
                  {emptyMessage}
                </div>
              ) : (
                <ul className="py-1">
                  {menuItems.map((tab) => {
                    const isActive = idsMatch(activeId, tab?.id);
                    const meta = resolveMenuMeta(tab);
                    return (
                      <li key={tab.id}>
                        <button
                          type="button"
                          onClick={() => {
                            if (onMenuSelect) onMenuSelect(tab);
                            else handleSelect(tab);
                            setShowAll(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 ${
                            isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                          }`}
                        >
                          <div className="font-medium">{resolveMenuTitle(tab)}</div>
                          {meta && <div className="text-[11px] text-gray-500">{meta}</div>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentTabs;
